import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionCard, ActionCardSkeleton } from './ActionCard';
import { CoachRequestTimesOverlay } from './CoachRequestTimes';
import { useAuth } from '../../contexts/AuthContext';
import { useChangeAnswer } from '../../hooks/useChangeAnswer';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { subscribeToBooking } from '../../services/liveCallBookings';
import { changeFromThreadProposal } from '../../utils/actionCard';
import { requestMove } from '../../utils/lessonIntent';
import { firstNameOf } from '../../utils/liveLessonText';
import { lessonProposalLine } from '../../utils/lessonTimes';
import type { Message } from '../../types/chat';
import type { LiveCallBooking } from '../../types/liveCall';

/**
 * A lesson's suggested times in the thread, as the Action card (decision 165, changes.md 2; contracts 6 and 15):
 * at the moment it was sent, the same card as at the top of the lesson's pages, kept as history once answered.
 * The server updates the message in place; a kept, replaced or moved one reads its own line, with no buttons.
 */
export function LessonTimeProposalBubble({ message }: { message: Message }) {
  const sent = message.lessonTimeProposal! as NonNullable<Message['lessonTimeProposal']> & { proposedBy?: 'coach' | 'player' };
  const { currentUser } = useAuth();
  const tz = useViewerZone();
  const navigate = useNavigate();
  const [b, setB] = useState<LiveCallBooking | null | undefined>(undefined);
  useEffect(() => subscribeToBooking(sent.bookingId, setB), [sent.bookingId]);
  const player = useUserIdentity(b?.playerId, 'player');
  const coach = useUserIdentity(b?.coachId, 'coach');
  const [picking, setPicking] = useState(false);
  const [now] = useState(() => Date.now());
  const isCoach = !!b && currentUser?.uid === b.coachId;
  const names = { coachFirst: firstNameOf(coach?.name ?? b?.coachName, 'Your coach'), playerFirst: firstNameOf(player?.name ?? b?.playerName, 'Your player') };
  const keptDay = new Date(sent.fromStartAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
  const result = lessonProposalLine({ status: sent.status, keptDay, isCoach, playerFirst: names.playerFirst });
  const change = changeFromThreadProposal(sent, names, message.createdAt ?? sent.fromStartAt, now, result);
  const answer = useChangeAnswer({ bookingOf: (id) => (b && b.id === id ? b : undefined), tz,
    onNoneWork: () => setPicking(true),
    onSuggestDifferent: (c) => { if (c.bookingId) { requestMove(c.bookingId); navigate(`/live/${c.bookingId}`); } } });

  // Which side reads it waits on the lesson; until then the card's own skeleton holds its place (changes.md 2).
  if (!b) return <div className="my-3 flex justify-center"><div className="w-full max-w-[320px]"><ActionCardSkeleton /></div></div>;
  return (
    <div className="my-3 flex justify-center" data-lesson-time-proposal={sent.status}>
      <ActionCard change={change} me={isCoach ? 'coach' : 'player'} tz={tz} onAnswer={answer}
        className="w-full max-w-[320px] border border-[#e6e6e6]" />
      {picking && (
        <CoachRequestTimesOverlay side="player" booking={b} tz={tz} onClose={() => setPicking(false)}
          playerFirst={names.playerFirst} coachFirst={names.coachFirst} />
      )}
    </div>
  );
}
